import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  sitesApi, circuitsApi,
  SITE_CATEGORIES, DIFFICULTIES, CAT_LABEL, DIFF_LABEL,
} from '../../services/tourismeApi';
import { getToken } from '../auth';
import './admin-form.css';

const EMPTY_SITE = {
  title: '', slug: '',
  category: 'culturel',
  shortDescription: '', description: '',
  address: '', lat: '', lng: '',
  openingHours: '', entryFee: '',
  phone: '', email: '',
  mainImageSeed: '', mainImageUrl: '',
  tips: '',
  featured: false, published: true, order: 0,
};

const EMPTY_CIRCUIT = {
  title: '', slug: '',
  shortDescription: '', description: '',
  duration: '', difficulty: 'facile',
  stops: [],
  mainImageSeed: '', mainImageUrl: '',
  featured: false, published: true, order: 0,
};

const CAT_PILL = {
  religieux:   'sunset',
  culturel:    'ocean',
  nature:      'ok',
  patrimoine:  'gold',
  gastronomie: '',
};

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

const Tourisme = () => {
  const token = getToken();
  const [tab, setTab] = useState('sites');

  if (!token) {
    return (
      <div className="content" style={{ padding: 40 }}>
        <p style={{ fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '.18em', color: 'var(--char-500)' }}>
          Session expirée. Reconnectez-vous.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="topbar">
        <div className="breadcrumb">
          <span>Cambérène</span><span className="sep">/</span>
          <span>Admin</span><span className="sep">/</span>
          <b>Tourisme</b>
        </div>
        <div className="top-actions">
          <div style={{ display: 'flex', gap: 4, padding: 4, background: 'var(--bone-2)', borderRadius: 999 }}>
            <button
              className="pill-btn"
              style={tab === 'sites' ? { background: 'var(--char-900)', color: 'var(--bone)' } : { background: 'transparent', border: 'none' }}
              onClick={() => setTab('sites')}
            >Sites</button>
            <button
              className="pill-btn"
              style={tab === 'circuits' ? { background: 'var(--char-900)', color: 'var(--bone)' } : { background: 'transparent', border: 'none' }}
              onClick={() => setTab('circuits')}
            >Circuits</button>
          </div>
        </div>
      </div>
      {tab === 'sites' ? <SitesPanel token={token} /> : <CircuitsPanel token={token} />}
    </>
  );
};

/* ============================================================
   PANEL : SITES
   ============================================================ */
const SitesPanel = ({ token }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filterCategory, setFilterCategory] = useState('all');
  const [filterFeatured, setFilterFeatured] = useState('all');
  const [query, setQuery] = useState('');

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_SITE);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await sitesApi.adminList(token, {
        category: filterCategory !== 'all' ? filterCategory : undefined,
        featured: filterFeatured === 'yes' ? 'yes' : undefined,
        q: query || undefined,
        limit: 200,
      });
      setItems(data.items); setTotal(data.total);
    } catch (err) { setError(err.message); }
    finally       { setLoading(false); }
  }, [token, filterCategory, filterFeatured, query]);

  useEffect(() => { load(); }, [load]);

  const kpi = useMemo(() => {
    const c = { total: items.length, religieux: 0, culturel: 0, nature: 0, featured: 0 };
    for (const s of items) {
      if (s.category === 'religieux') c.religieux++;
      if (s.category === 'culturel')  c.culturel++;
      if (s.category === 'nature')    c.nature++;
      if (s.featured)                 c.featured++;
    }
    return c;
  }, [items]);

  const openCreate = () => { setEditingId(null); setForm(EMPTY_SITE); setSaveError(null); setOpen(true); };
  const openEdit = async (id) => {
    setEditingId(id); setSaveError(null);
    try {
      const { site } = await sitesApi.adminGet(token, id);
      setForm({
        title: site.title, slug: site.slug,
        category: site.category,
        shortDescription: site.shortDescription || '', description: site.description || '',
        address: site.address || '',
        lat: site.lat ?? '', lng: site.lng ?? '',
        openingHours: site.openingHours || '', entryFee: site.entryFee || '',
        phone: site.phone || '', email: site.email || '',
        mainImageSeed: site.mainImageSeed || '', mainImageUrl: site.mainImageUrl || '',
        tips: site.tips || '',
        featured: site.featured, published: site.published, order: site.order || 0,
      });
      setOpen(true);
    } catch (err) { alert('Impossible de charger : ' + err.message); }
  };

  const close = useCallback(() => { setOpen(false); setEditingId(null); setForm(EMPTY_SITE); setSaveError(null); }, []);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = ''; window.removeEventListener('keydown', onKey); };
  }, [open, close]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setSaveError(null);
    try {
      const payload = {
        ...form,
        slug: form.slug || undefined,
        lat: form.lat === '' ? null : Number(form.lat),
        lng: form.lng === '' ? null : Number(form.lng),
        order: parseInt(form.order, 10) || 0,
      };
      if (editingId) await sitesApi.adminUpdate(token, editingId, payload);
      else           await sitesApi.adminCreate(token, payload);
      close(); await load();
    } catch (err) { setSaveError(err.message); }
    finally       { setSaving(false); }
  };

  const onDelete = async (id, title) => {
    if (!window.confirm(`Supprimer définitivement « ${title} » ?`)) return;
    try { await sitesApi.adminDelete(token, id); await load(); }
    catch (err) { alert(err.message); }
  };
  const onToggleFeatured  = async (s) => { try { await sitesApi.adminUpdate(token, s.id, { featured: !s.featured });  load(); } catch (e) { alert(e.message); } };
  const onTogglePublished = async (s) => { try { await sitesApi.adminUpdate(token, s.id, { published: !s.published }); load(); } catch (e) { alert(e.message); } };

  return (
    <div className="content">
      <div className="page-head">
        <div>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.22em', textTransform: 'uppercase', color: 'var(--char-500)' }}>
            Tourisme · Sites · {total}
          </span>
          <h1>Les lieux,<br /><span style={{ fontStyle: 'italic' }}>en fiche.</span></h1>
          <p className="sub">Mausolée, mosquée, débarcadère, plage, marché — tout site visitable du village.</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Chercher…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pill-btn"
            style={{ background: 'var(--bone)', border: '1px solid var(--line)', padding: '7px 14px', minWidth: 180 }}
          />
          <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
            className="pill-btn" style={{ background: 'var(--bone)', border: '1px solid var(--line)', padding: '7px 14px' }}>
            {SITE_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.lbl}</option>)}
          </select>
          <select value={filterFeatured} onChange={(e) => setFilterFeatured(e.target.value)}
            className="pill-btn" style={{ background: 'var(--bone)', border: '1px solid var(--line)', padding: '7px 14px' }}>
            <option value="all">Tous</option>
            <option value="yes">★ Vedettes</option>
          </select>
          <button className="top-cta" onClick={openCreate} style={{ marginLeft: 4 }}>
            <span className="plus" /> Nouveau site
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
        {[
          { lbl: 'Total',     val: kpi.total,     color: 'var(--char-400)' },
          { lbl: 'Religieux', val: kpi.religieux, color: '#E76F51' },
          { lbl: 'Culturel',  val: kpi.culturel,  color: '#1B6985' },
          { lbl: 'Vedettes',  val: kpi.featured,  color: '#C9A961' },
        ].map((k) => (
          <div key={k.lbl} className="kpi">
            <span className="lab"><span className="dot" style={{ background: k.color }} />{k.lbl}</span>
            <b>{k.val}</b>
          </div>
        ))}
      </div>

      {error && (
        <div className="bulkbar on" style={{ background: 'rgba(230,57,70,.12)', color: '#B72333' }}>
          <span>⚠</span><span>{error}</span>
        </div>
      )}

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Titre</th>
              <th>Catégorie</th>
              <th>Adresse</th>
              <th>★</th>
              <th>Statut</th>
              <th>MAJ</th>
              <th style={{ width: 120 }} />
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40, color: 'var(--char-500)', fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '.18em' }}>Chargement…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40, color: 'var(--char-500)' }}>Aucun site.</td></tr>}
            {items.map((s) => (
              <tr key={s.id} onClick={() => openEdit(s.id)}>
                <td>
                  <b style={{ fontWeight: 500, fontSize: 14 }}>{s.title}</b>
                  <small style={{ display: 'block', marginTop: 2, fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--char-500)' }}>/{s.slug}</small>
                </td>
                <td><span className={`cat-pill ${CAT_PILL[s.category] || ''}`}>{CAT_LABEL[s.category] || s.category}</span></td>
                <td style={{ fontSize: 12, color: 'var(--char-700)' }}>{s.address || '—'}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => onToggleFeatured(s)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 18, color: s.featured ? 'var(--sunset)' : 'var(--char-400)' }}>★</button>
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  <span className={`status-pill ${s.published ? 'live' : 'muted'}`} style={{ cursor: 'pointer' }} onClick={() => onTogglePublished(s)}>
                    {s.published ? 'Publié' : 'Brouillon'}
                  </span>
                </td>
                <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--char-500)' }}>{fmtDate(s.updatedAt)}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  <div className="row-actions" style={{ opacity: 1 }}>
                    <button title="Éditer" onClick={() => openEdit(s.id)}>✎</button>
                    <button title="Supprimer" className="danger" onClick={() => onDelete(s.id, s.title)}>✕</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="table-foot">
          <span>{total} site{total > 1 ? 's' : ''}</span>
          <span>{items.length} affiché{items.length > 1 ? 's' : ''}</span>
        </div>
      </div>

      {open && (
        <div className="admin-modal-bg" onClick={close}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 820 }}>
            <div className="admin-modal-head">
              <div>
                <span className="admin-modal-eyebrow">{editingId ? 'Édition' : 'Nouveau site'}</span>
                <h2 className="admin-modal-title">{editingId ? form.title : 'Ajouter un site touristique'}</h2>
              </div>
              <button onClick={close} className="admin-modal-close">✕</button>
            </div>
            <form className="admin-form" onSubmit={onSubmit}>
              <div className="admin-form-row" style={{ gridTemplateColumns: '1.4fr 1fr' }}>
                <label className="admin-field">
                  <span className="admin-field-label">Titre *</span>
                  <input type="text" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} required />
                </label>
                <label className="admin-field">
                  <span className="admin-field-label">Catégorie *</span>
                  <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
                    {SITE_CATEGORIES.filter((c) => c.id !== 'all').map((c) => <option key={c.id} value={c.id}>{c.lbl}</option>)}
                  </select>
                </label>
              </div>
              <label className="admin-field">
                <span className="admin-field-label">Slug (laissez vide pour auto)</span>
                <input type="text" value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))} placeholder="ex : mausolee-camberene" />
              </label>
              <label className="admin-field">
                <span className="admin-field-label">Description courte (lede)</span>
                <textarea rows={2} value={form.shortDescription} onChange={(e) => setForm((f) => ({ ...f, shortDescription: e.target.value }))} placeholder="Phrase d'accroche affichée dans la liste." />
              </label>
              <label className="admin-field">
                <span className="admin-field-label">Description complète</span>
                <textarea rows={5} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
              </label>
              <div className="admin-form-row" style={{ gridTemplateColumns: '1.6fr 1fr 1fr' }}>
                <label className="admin-field">
                  <span className="admin-field-label">Adresse</span>
                  <input type="text" value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} />
                </label>
                <label className="admin-field">
                  <span className="admin-field-label">Latitude</span>
                  <input type="text" value={form.lat} onChange={(e) => setForm((f) => ({ ...f, lat: e.target.value }))} placeholder="14.7724" />
                </label>
                <label className="admin-field">
                  <span className="admin-field-label">Longitude</span>
                  <input type="text" value={form.lng} onChange={(e) => setForm((f) => ({ ...f, lng: e.target.value }))} placeholder="-17.4480" />
                </label>
              </div>
              <div className="admin-form-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <label className="admin-field">
                  <span className="admin-field-label">Horaires</span>
                  <input type="text" value={form.openingHours} onChange={(e) => setForm((f) => ({ ...f, openingHours: e.target.value }))} placeholder="Lun-Ven 9h-17h" />
                </label>
                <label className="admin-field">
                  <span className="admin-field-label">Tarif d'entrée</span>
                  <input type="text" value={form.entryFee} onChange={(e) => setForm((f) => ({ ...f, entryFee: e.target.value }))} placeholder="Gratuit · 1 000 FCFA · Sur invitation" />
                </label>
              </div>
              <div className="admin-form-row" style={{ gridTemplateColumns: '1fr 1.4fr' }}>
                <label className="admin-field">
                  <span className="admin-field-label">Téléphone</span>
                  <input type="text" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
                </label>
                <label className="admin-field">
                  <span className="admin-field-label">Email</span>
                  <input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
                </label>
              </div>
              <div className="admin-form-row" style={{ gridTemplateColumns: '1fr 1.6fr' }}>
                <label className="admin-field">
                  <span className="admin-field-label">Image seed (picsum)</span>
                  <input type="text" value={form.mainImageSeed} onChange={(e) => setForm((f) => ({ ...f, mainImageSeed: e.target.value }))} placeholder="cb-tour-…" />
                </label>
                <label className="admin-field">
                  <span className="admin-field-label">Ou URL image directe</span>
                  <input type="url" value={form.mainImageUrl} onChange={(e) => setForm((f) => ({ ...f, mainImageUrl: e.target.value }))} placeholder="https://…" />
                </label>
              </div>
              <label className="admin-field">
                <span className="admin-field-label">Conseils visiteurs (optionnel)</span>
                <textarea rows={2} value={form.tips} onChange={(e) => setForm((f) => ({ ...f, tips: e.target.value }))} />
              </label>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <label className="admin-publish-toggle">
                  <input type="checkbox" checked={form.featured} onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))} />
                  <span>{form.featured ? '★ En vedette' : 'Mettre en vedette'}</span>
                </label>
                <label className="admin-publish-toggle">
                  <input type="checkbox" checked={form.published} onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))} />
                  <span>{form.published ? 'Publié' : 'Brouillon'}</span>
                </label>
              </div>
              {saveError && <div className="admin-form-error">{saveError}</div>}
              <div className="admin-form-foot">
                <button type="button" onClick={close} className="admin-btn">Annuler</button>
                <button type="submit" disabled={saving} className="admin-btn admin-btn-primary">
                  {saving ? 'Enregistrement…' : (editingId ? 'Enregistrer' : 'Créer le site')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

/* ============================================================
   PANEL : CIRCUITS
   ============================================================ */
const CircuitsPanel = ({ token }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterDifficulty, setFilterDifficulty] = useState('all');
  const [query, setQuery] = useState('');

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_CIRCUIT);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await circuitsApi.adminList(token, {
        difficulty: filterDifficulty !== 'all' ? filterDifficulty : undefined,
        q: query || undefined,
        limit: 200,
      });
      setItems(data.items); setTotal(data.total);
    } catch (err) { setError(err.message); }
    finally       { setLoading(false); }
  }, [token, filterDifficulty, query]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditingId(null); setForm(EMPTY_CIRCUIT); setSaveError(null); setOpen(true); };
  const openEdit = async (id) => {
    setEditingId(id); setSaveError(null);
    try {
      const { circuit } = await circuitsApi.adminGet(token, id);
      setForm({
        title: circuit.title, slug: circuit.slug,
        shortDescription: circuit.shortDescription || '', description: circuit.description || '',
        duration: circuit.duration || '', difficulty: circuit.difficulty,
        stops: circuit.stops || [],
        mainImageSeed: circuit.mainImageSeed || '', mainImageUrl: circuit.mainImageUrl || '',
        featured: circuit.featured, published: circuit.published, order: circuit.order || 0,
      });
      setOpen(true);
    } catch (err) { alert('Impossible de charger : ' + err.message); }
  };

  const close = useCallback(() => { setOpen(false); setEditingId(null); setForm(EMPTY_CIRCUIT); setSaveError(null); }, []);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = ''; window.removeEventListener('keydown', onKey); };
  }, [open, close]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setSaveError(null);
    try {
      const payload = {
        ...form,
        slug: form.slug || undefined,
        stops: form.stops.filter((st) => st.title),
        order: parseInt(form.order, 10) || 0,
      };
      if (editingId) await circuitsApi.adminUpdate(token, editingId, payload);
      else           await circuitsApi.adminCreate(token, payload);
      close(); await load();
    } catch (err) { setSaveError(err.message); }
    finally       { setSaving(false); }
  };

  const onDelete = async (id, title) => {
    if (!window.confirm(`Supprimer définitivement « ${title} » ?`)) return;
    try { await circuitsApi.adminDelete(token, id); await load(); }
    catch (err) { alert(err.message); }
  };

  const addStop    = () => setForm((f) => ({ ...f, stops: [...f.stops, { title: '', description: '', order: f.stops.length }] }));
  const removeStop = (i) => setForm((f) => ({ ...f, stops: f.stops.filter((_, idx) => idx !== i) }));
  const updateStop = (i, patch) => setForm((f) => ({ ...f, stops: f.stops.map((st, idx) => idx === i ? { ...st, ...patch } : st) }));

  return (
    <div className="content">
      <div className="page-head">
        <div>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.22em', textTransform: 'uppercase', color: 'var(--char-500)' }}>
            Tourisme · Circuits · {total}
          </span>
          <h1>Les parcours,<br /><span style={{ fontStyle: 'italic' }}>en étapes.</span></h1>
          <p className="sub">Itinéraires guidés — spirituel, village &amp; océan, journée complète.</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text" placeholder="Chercher…" value={query} onChange={(e) => setQuery(e.target.value)}
            className="pill-btn"
            style={{ background: 'var(--bone)', border: '1px solid var(--line)', padding: '7px 14px', minWidth: 180 }}
          />
          <select value={filterDifficulty} onChange={(e) => setFilterDifficulty(e.target.value)}
            className="pill-btn" style={{ background: 'var(--bone)', border: '1px solid var(--line)', padding: '7px 14px' }}>
            {DIFFICULTIES.map((d) => <option key={d.id} value={d.id}>{d.lbl}</option>)}
          </select>
          <button className="top-cta" onClick={openCreate} style={{ marginLeft: 4 }}>
            <span className="plus" /> Nouveau circuit
          </button>
        </div>
      </div>

      {error && (
        <div className="bulkbar on" style={{ background: 'rgba(230,57,70,.12)', color: '#B72333' }}>
          <span>⚠</span><span>{error}</span>
        </div>
      )}

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Titre</th>
              <th>Durée</th>
              <th>Difficulté</th>
              <th>Étapes</th>
              <th>★</th>
              <th>Statut</th>
              <th style={{ width: 120 }} />
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40, color: 'var(--char-500)', fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '.18em' }}>Chargement…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40, color: 'var(--char-500)' }}>Aucun circuit.</td></tr>}
            {items.map((c) => (
              <tr key={c.id} onClick={() => openEdit(c.id)}>
                <td>
                  <b style={{ fontWeight: 500, fontSize: 14 }}>{c.title}</b>
                  <small style={{ display: 'block', marginTop: 2, fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--char-500)' }}>/{c.slug}</small>
                </td>
                <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--char-700)' }}>{c.duration || '—'}</td>
                <td><span className="cat-pill">{DIFF_LABEL[c.difficulty] || c.difficulty}</span></td>
                <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--char-700)' }}>{c.stops?.length || 0}</td>
                <td>{c.featured && <span style={{ color: 'var(--sunset)' }}>★</span>}</td>
                <td><span className={`status-pill ${c.published ? 'live' : 'muted'}`}>{c.published ? 'Publié' : 'Brouillon'}</span></td>
                <td onClick={(e) => e.stopPropagation()}>
                  <div className="row-actions" style={{ opacity: 1 }}>
                    <button title="Éditer" onClick={() => openEdit(c.id)}>✎</button>
                    <button title="Supprimer" className="danger" onClick={() => onDelete(c.id, c.title)}>✕</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="table-foot">
          <span>{total} circuit{total > 1 ? 's' : ''}</span>
        </div>
      </div>

      {open && (
        <div className="admin-modal-bg" onClick={close}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 760 }}>
            <div className="admin-modal-head">
              <div>
                <span className="admin-modal-eyebrow">{editingId ? 'Édition' : 'Nouveau circuit'}</span>
                <h2 className="admin-modal-title">{editingId ? form.title : 'Créer un circuit'}</h2>
              </div>
              <button onClick={close} className="admin-modal-close">✕</button>
            </div>
            <form className="admin-form" onSubmit={onSubmit}>
              <label className="admin-field">
                <span className="admin-field-label">Titre *</span>
                <input type="text" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} required />
              </label>
              <label className="admin-field">
                <span className="admin-field-label">Slug (laissez vide pour auto)</span>
                <input type="text" value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))} />
              </label>
              <label className="admin-field">
                <span className="admin-field-label">Description courte</span>
                <textarea rows={2} value={form.shortDescription} onChange={(e) => setForm((f) => ({ ...f, shortDescription: e.target.value }))} />
              </label>
              <label className="admin-field">
                <span className="admin-field-label">Description complète</span>
                <textarea rows={4} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
              </label>
              <div className="admin-form-row" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                <label className="admin-field">
                  <span className="admin-field-label">Durée</span>
                  <input type="text" value={form.duration} onChange={(e) => setForm((f) => ({ ...f, duration: e.target.value }))} placeholder="2h · demi-journée · 1 journée" />
                </label>
                <label className="admin-field">
                  <span className="admin-field-label">Difficulté</span>
                  <select value={form.difficulty} onChange={(e) => setForm((f) => ({ ...f, difficulty: e.target.value }))}>
                    {DIFFICULTIES.filter((d) => d.id !== 'all').map((d) => <option key={d.id} value={d.id}>{d.lbl}</option>)}
                  </select>
                </label>
                <label className="admin-field">
                  <span className="admin-field-label">Ordre d'affichage</span>
                  <input type="number" value={form.order} onChange={(e) => setForm((f) => ({ ...f, order: e.target.value }))} />
                </label>
              </div>

              <div className="admin-field">
                <span className="admin-field-label" style={{ display: 'flex', justifyContent: 'space-between' }}>
                  Étapes ({form.stops.length})
                  <button type="button" onClick={addStop} className="admin-btn" style={{ padding: '4px 10px', fontSize: 11 }}>+ Étape</button>
                </span>
                {form.stops.length === 0 && (
                  <small style={{ color: 'var(--char-500)', fontStyle: 'italic' }}>Aucune étape. Ajoutez les arrêts du parcours.</small>
                )}
                {form.stops.map((st, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '60px 1fr 2fr auto', gap: 8, marginTop: 6 }}>
                    <input type="number" value={st.order ?? i} onChange={(e) => updateStop(i, { order: parseInt(e.target.value, 10) || 0 })} />
                    <input type="text" placeholder="Titre étape" value={st.title} onChange={(e) => updateStop(i, { title: e.target.value })} />
                    <input type="text" placeholder="Description" value={st.description} onChange={(e) => updateStop(i, { description: e.target.value })} />
                    <button type="button" onClick={() => removeStop(i)} className="admin-btn" style={{ padding: '4px 10px', color: '#B72333' }}>✕</button>
                  </div>
                ))}
              </div>

              <div className="admin-form-row" style={{ gridTemplateColumns: '1fr 1.4fr' }}>
                <label className="admin-field">
                  <span className="admin-field-label">Image seed</span>
                  <input type="text" value={form.mainImageSeed} onChange={(e) => setForm((f) => ({ ...f, mainImageSeed: e.target.value }))} />
                </label>
                <label className="admin-field">
                  <span className="admin-field-label">Ou URL image</span>
                  <input type="url" value={form.mainImageUrl} onChange={(e) => setForm((f) => ({ ...f, mainImageUrl: e.target.value }))} />
                </label>
              </div>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <label className="admin-publish-toggle">
                  <input type="checkbox" checked={form.featured} onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))} />
                  <span>{form.featured ? '★ En vedette' : 'Mettre en vedette'}</span>
                </label>
                <label className="admin-publish-toggle">
                  <input type="checkbox" checked={form.published} onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))} />
                  <span>{form.published ? 'Publié' : 'Brouillon'}</span>
                </label>
              </div>
              {saveError && <div className="admin-form-error">{saveError}</div>}
              <div className="admin-form-foot">
                <button type="button" onClick={close} className="admin-btn">Annuler</button>
                <button type="submit" disabled={saving} className="admin-btn admin-btn-primary">
                  {saving ? 'Enregistrement…' : (editingId ? 'Enregistrer' : 'Créer le circuit')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tourisme;
